import React, { useEffect } from "react";
import { Helmet } from "react-helmet";
import styled, { keyframes } from "styled-components";
import { prop } from "styled-tools";
import debounce from "lodash/debounce";

import BarStackedChartGroup from "components/BarStackedChartGroup";
import Footer from "components/Footer";
import LazyLoad from "react-lazyload";
import ExplorePromo from "../components/ExplorePromo";
import Nav from "components/Nav";
import SurveyData from "components/SurveyData";
import Title from "components/Title";

/*
 * Styles
 */

/* Container */

const enterContainer = keyframes`
  from {
    opacity: 0;
  }
`;

const Container = styled.div`
  animation: ${enterContainer} 1800ms ease 100ms backwards;
  padding: 0 2rem;
`;

/* Content */

const Content = styled.div`
  margin: 0 auto 4rem;
  max-width: 50rem;
  position: relative;
  top: 4.8rem;
`;

/* Chart */

const Chart = styled.div`
  min-height: 15.6rem;
  margin: 2rem 0 0;
  padding-bottom: 1px;
  @media (min-width: 66em) {
    margin-left: -4rem;
    margin-right: -4rem;
  }
  @media (min-width: 76em) {
    margin-left: -8rem;
    margin-right: -8rem;
  }
  @media (min-width: 86em) {
    margin-left: -12rem;
    margin-right: -12rem;
  }
`;

/* Statement */

const Statement = styled.div`
  margin-top: 2rem;
  @media (min-width: 40em) {
    align-items: flex-end;
    display: flex;
    justify-content: space-between;
    gap: 4rem;
    > * {
      flex: 1 1 50%;
    }
  }
`;

const StatementFinding = styled.p`
  background-color: ${prop("theme.colors.grayTTT")};
  margin: 0 0 1.6rem;
  padding: 1.2rem;
  strong {
    color: ${prop("theme.colors.grayS")};
    display: block;
  }
  @media (min-width: 40em) {
    font-size: ${prop("theme.scale.down1")};
    line-height: 1.2rem;
  }
`;

const StatementHeading = styled.h5`
  color: ${prop("theme.colors.grayS")};
  font-family: ${prop("theme.fonts.heading")};
  font-size: ${prop("theme.scale.up1")};
  font-weight: normal;
  margin: 0 0 1.6rem;
`;

/* Headings */

const H2 = styled.h2`
  color: ${prop("theme.colors.grayS")};
  font-family: ${prop("theme.fonts.heading")};
  font-size: ${prop("theme.scale.up4")};
  line-height: 2.8rem;
  margin: 4rem 0 2rem;
`;

const H3 = styled.h3`
  color: ${prop("theme.colors.grayS")};
  font-family: ${prop("theme.fonts.heading")};
  font-size: ${prop("theme.scale.up2")};
  line-height: 2rem;
  margin: 0 0 0.8rem;
`;

const H4 = styled.h4`
  font-size: ${prop("theme.scale.down1")};
  letter-spacing: 0.125em;
  line-height: 2rem;
  margin: 0 0 0.8rem;
  position: relative;
  text-transform: uppercase;
  &::after {
    background-color: ${prop("theme.colors.grayT")};
    bottom: 0;
    content: "";
    height: 1px;
    left: 0;
    position: absolute;
    width: 2rem;
  }
`;

/* Intro */

const Intro = styled.p`
  color: ${prop("theme.colors.grayS")};
  font-family: ${prop("theme.fonts.heading")};
  font-size: ${prop("theme.scale.up2")};
  line-height: 2.4rem;
  margin: 2rem 0 4rem;
  @media (min-width: 40em) {
    font-size: ${prop("theme.scale.up3")};
    line-height: 2.8rem;
  }
`;

/* P */

const P = styled.p`
  font-size: ${prop("theme.scale.up1")};
  line-height: 2rem;
  margin: 0 0 2rem;
`;

/* More */

const More = styled.p`
  font-size: ${prop("theme.scale.down1")};
  margin: 0 0 3.2rem;
  text-align: center;
`;

/* UL */

const UL = styled.ul`
  line-height: 1.6rem;
  margin: 0 0 2rem;
  li + li {
    margin-top: 0.8rem;
  }
`;

/*
 * Component
 */

const searchAll = [
  ["AGE", ["1", "2", "3", "4"]],
  ["MF", ["1", "2", "3"]],
  ["REGION", ["1", "2", "3", "4"]],
  ["EDUCATION", ["1", "2", "3", "4", "5"]],
  ["INCOME", ["1", "2", "3", "4", "5", "6", "7"]],
  ["MARITAL", ["1", "2", "3", "4", "5", "6"]],
  ["ETHNICITY", ["1", "2", "3", "4", "5"]],
  ["RELTRAD", ["1", "2", "3", "4", "5"]],
  ["ATTENDANCE", ["1", "2", "3", "4", "5", "6", "7"]],
];

const searchEvangelical = [
  ["AGE", ["1", "2", "3", "4"]],
  ["MF", ["1", "2", "3"]],
  ["REGION", ["1", "2", "3", "4"]],
  ["EDUCATION", ["1", "2", "3", "4", "5"]],
  ["INCOME", ["1", "2", "3", "4", "5", "6", "7"]],
  ["MARITAL", ["1", "2", "3", "4", "5", "6"]],
  ["ETHNICITY", ["1", "2", "3", "4", "5"]],
  ["RELTRAD", ["1", "2", "3", "4", "5"]],
  ["EVB", ["1"]],
  ["ATTENDANCE", ["1", "2", "3", "4", "5", "6", "7"]],
];

const searchMillenial = [
  ["AGE", ["1"]],
  ["MF", ["1", "2", "3"]],
  ["REGION", ["1", "2", "3", "4"]],
  ["EDUCATION", ["1", "2", "3", "4", "5"]],
  ["INCOME", ["1", "2", "3", "4", "5", "6", "7"]],
  ["MARITAL", ["1", "2", "3", "4", "5", "6"]],
  ["ETHNICITY", ["1", "2", "3", "4", "5"]],
  ["RELTRAD", ["1", "2", "3", "4", "5"]],
  ["ATTENDANCE", ["1", "2", "3", "4", "5", "6", "7"]],
];

const evangelicalQueryString =
  "?AGE=30&MF=14&REGION=30&DENSITY=62&EDUCATION=62&INCOME=254&MARITAL=126&ETHNICITY=62&RELTRAD=62&EVB=2&ATTENDANCE=254";

const linkifyESV = debounce(() => {
  window.dispatchEvent(new Event("esv-crossref.trigger-linkify"));
}, 1000);

const KeyFindings = (props) => {
  const { locale } = props;
  const availableYears =
    process.env[
      `REACT_APP_SURVEY_AVAILABLE_YEARS_${locale.toUpperCase()}`
    ].split(",");

  useEffect(() => {
    linkifyESV();
  }, []);

  return (
    <React.Fragment>
      <Helmet>
        <title>The State of Theology</title>
        <meta
          name="description"
          content="What do Americans believe about God, salvation, ethics, and the Bible?"
        />
        <meta property="og:title" content="The State of Theology" />
        <meta property="og:url" content="https://thestateoftheology.com" />
        <meta
          property="og:description"
          content="What do Americans believe about God, salvation, ethics, and the Bible?"
        />
        <meta name="twitter:title" content="The State of Theology" />
        <meta
          name="twitter:description"
          content="What do Americans believe about God, salvation, ethics, and the Bible?"
        />
        <meta name="twitter:domain" content="TheStateOfTheology.com" />
        <meta name="twitter:url" content="https://thestateoftheology.com" />
      </Helmet>
      <Nav />
      <SurveyData locale={locale} years={availableYears}>
        {(data, loading) => {
          if (loading) return;

          return (
            <Container>
              <Content>
                <Title
                  statements={data["2022"].statements}
                  responses={data["2022"].responses}
                />

                <Intro>
                  What do Americans believe about God, salvation, ethics, and
                  the Bible? Ligonier Ministries and LifeWay Research partnered
                  to find out. Every two years, we take the theological
                  temperature of the United States to help Christians better
                  understand today’s culture and to equip the church with better
                  insights for discipleship. Read some of our key findings from
                  2022 below and explore the data for yourself.
                </Intro>

                <H2>Does God Change?</H2>

                <P>
                  As we look at ourselves and at the world, it is clear that
                  human beings, along with the rest of creation, undergo
                  frequent changes. But does this principle of change apply to
                  God as well?
                </P>

                <P>
                  The Bible affirms the truth that the triune God is both
                  omniscient, meaning that He knows all things, and immutable,
                  meaning that He cannot and does not change (Isa. 46:10; Mal.
                  3:6; James 1:17; 1 John 3:20). Despite this truth, the
                  majority of adults in the United States believe that God both
                  learns and adapts to different circumstances.
                </P>

                <Statement>
                  <div>
                    <H4>Statement No. 4</H4>
                    <StatementHeading>
                      God learns and adapts to different circumstances.
                    </StatementHeading>
                  </div>
                  <StatementFinding>
                    <strong>U.S. Adult Finding:</strong>
                    51% agree vs. 32% disagree
                  </StatementFinding>
                </Statement>

                <Chart>
                  <LazyLoad offset={200}>
                    <BarStackedChartGroup
                      data={[["Q21_4", data["2022"].responses]]}
                      locale={locale}
                      search={searchAll}
                      population="U.S. Adult Respondents"
                      years={["2022"]}
                    />
                  </LazyLoad>
                </Chart>

                <More>
                  <a href="/data-explorer/2022/4">View in Data Explorer</a>
                </More>

                <P>
                  Despite the clear teaching of Scripture, this year’s survey
                  reveals that approximately half of evangelicals believe that
                  God learns and adapts to various situations, meaning that they
                  believe that God <em>does</em> change.
                </P>

                <Statement>
                  <div>
                    <H4>Statement No. 4</H4>
                    <StatementHeading>
                      God learns and adapts to different circumstances.
                    </StatementHeading>
                  </div>
                  <StatementFinding>
                    <strong>U.S. Evangelical Finding:</strong>
                    48% agree vs. 43% disagree
                  </StatementFinding>
                </Statement>

                <Chart>
                  <LazyLoad offset={200}>
                    <BarStackedChartGroup
                      data={[["Q21_4", data["2022"].responses]]}
                      locale={locale}
                      search={searchEvangelical}
                      population="U.S. Evangelical Respondents"
                      years={["2022"]}
                    />
                  </LazyLoad>
                </Chart>

                <More>
                  <a href={`/data-explorer/2022/4${evangelicalQueryString}`}>
                    View in Data Explorer
                  </a>
                </More>

                <P>
                  These results show that American evangelicals and the general
                  U.S. population are essentially equivalent in their agreement
                  with this statement. Nearly half of both groups believe that
                  God changes by learning and adapting. This may indicate the
                  influence of open theism (which denies God’s complete
                  knowledge of future events) and process theology (which denies
                  God’s omnipotence and asserts that He does undergo changes)
                  within the evangelical church. This finding may also indicate
                  a lack of clear, biblical teaching on the character of God in
                  evangelical churches.
                </P>

                <H2>Are We Born Innocent?</H2>

                <P>
                  When God created the world, everything He made was good (Gen.
                  1:10, 21, 25, 31). Yet through Adam and Eve’s rebellion in the
                  garden of Eden, humankind became corrupted. The Bible teaches
                  the concept of original sin, which means that since the fall,
                  every human being inherits a sin nature from the time of their
                  conception (Ps. 51:5; Rom. 5:12). In other words, we are not
                  sinners because we sin; rather, we sin because we are sinners.
                </P>

                <Statement>
                  <div>
                    <H4>Statement No. 15</H4>
                    <StatementHeading>
                      Everyone is born innocent in the eyes of God.
                    </StatementHeading>
                  </div>
                  <StatementFinding>
                    <strong>U.S. Adult Finding:</strong>
                    71% agree vs. 21% disagree
                  </StatementFinding>
                </Statement>

                <Chart>
                  <LazyLoad offset={200}>
                    <BarStackedChartGroup
                      data={[["Q24_4", data["2022"].responses]]}
                      locale={locale}
                      search={searchAll}
                      population="U.S. Adult Respondents"
                      years={["2022"]}
                    />
                  </LazyLoad>
                </Chart>

                <More>
                  <a href="/data-explorer/2022/15">View in Data Explorer</a>
                </More>

                <P>
                  It is unsurprising that most U.S. adults believe that humans
                  are born innocent, given the influence of humanistic
                  philosophies and worldviews that teach self-determinism and a
                  view of humankind as basically good.
                </P>

                <Statement>
                  <div>
                    <H4>Statement No. 15</H4>
                    <StatementHeading>
                      Everyone is born innocent in the eyes of God.
                    </StatementHeading>
                  </div>
                  <StatementFinding>
                    <strong>U.S. Evangelical Finding:</strong>
                    65% agree vs. 32% disagree
                  </StatementFinding>
                </Statement>

                <Chart>
                  <LazyLoad offset={200}>
                    <BarStackedChartGroup
                      data={[["Q24_4", data["2022"].responses]]}
                      locale={locale}
                      search={searchEvangelical}
                      population="U.S. Evangelical Respondents"
                      years={["2022"]}
                    />
                  </LazyLoad>
                </Chart>

                <More>
                  <a href={`/data-explorer/2022/15${evangelicalQueryString}`}>
                    View in Data Explorer
                  </a>
                </More>

                <P>
                  The fact that almost two-thirds of evangelicals believe that
                  humans are born in a state of innocence reveals that the
                  biblical teaching of original sin is not embraced by most
                  evangelicals. God’s Word, however, makes clear that all humans
                  are “by nature children of wrath” (Eph. 2:3). This truth is
                  foundational for an accurate understanding of the gospel and
                  of our absolute need for the grace of God in salvation.
                </P>

                <H2>Does Church Membership Matter?</H2>

                <P>
                  Jesus says in Matthew 16:18, “I will build my church.” But
                  what is the church? Biblically speaking, the church consists
                  of those whom God has called out of the world and drawn to
                  Himself through Christ. The universal church, which consists
                  of every person past, present, and future whom God calls to
                  Himself, is expressed tangibly through local church bodies
                  that extend around the world.
                </P>

                <Statement>
                  <div>
                    <H4>Statement No. 24</H4>
                    <StatementHeading>
                      Every Christian has an obligation to join a local church.
                    </StatementHeading>
                  </div>
                  <StatementFinding>
                    <strong>U.S. Adult Finding:</strong>
                    36% agree vs. 56% disagree
                  </StatementFinding>
                </Statement>

                <Chart>
                  <LazyLoad offset={200}>
                    <BarStackedChartGroup
                      data={[["Q27_3", data["2022"].responses]]}
                      locale={locale}
                      search={searchAll}
                      population="U.S. Adult Respondents"
                      years={["2022"]}
                    />
                  </LazyLoad>
                </Chart>

                <More>
                  <a href="/data-explorer/2022/24">View in Data Explorer</a>
                </More>

                <P>
                  For much of American history, the influence of Christianity
                  resulted in a high rate of church attendance, and church
                  membership was normative. Yet increasing secularization in the
                  United States has led to more Americans’ identifying as
                  nonreligious. In addition, the entrenched cultural value of
                  individualism makes it unsurprising that most Americans deem
                  church membership as optional for Christians.
                </P>

                <Statement>
                  <div>
                    <H4>Statement No. 24</H4>
                    <StatementHeading>
                      Every Christian has an obligation to join a local church.
                    </StatementHeading>
                  </div>
                  <StatementFinding>
                    <strong>U.S. Evangelical Finding:</strong>
                    68% agree vs. 26% disagree
                  </StatementFinding>
                </Statement>

                <Chart>
                  <LazyLoad offset={200}>
                    <BarStackedChartGroup
                      data={[["Q27_3", data["2022"].responses]]}
                      locale={locale}
                      search={searchEvangelical}
                      population="U.S. Evangelical Respondents"
                      years={["2022"]}
                    />
                  </LazyLoad>
                </Chart>

                <More>
                  <a href={`/data-explorer/2022/24${evangelicalQueryString}`}>
                    View in Data Explorer
                  </a>
                </More>

                <P>
                  Given the Bible’s assumption that those who claim to follow
                  Christ will also join a local body of believers (1 Cor.
                  5:12–13; 11:18; 1 Tim. 3:1–13; 5:9; 1 Peter 5:1–5), it may be
                  surprising that only 68 percent of evangelicals view church
                  membership as obligatory. This may indicate the influence of
                  an individualistic worldview within the church, as well as
                  challenges posed by the COVID-19 pandemic and ever-increasing
                  access to viewing various churches’ services via the internet.
                </P>

                <H2>U.S. Adults: Trends</H2>

                <P>
                  The State of Theology survey has been conducted every two
                  years since our first U.S. survey in 2014. Survey results over
                  the past eight years reveal that Americans increasingly reject
                  the Bible’s divine origin and more readily embrace sexual
                  behavior that is condemned in Scripture.
                </P>

                <H3>The Bible</H3>

                <P>
                  U.S. adults increasingly reject the divine authorship of the
                  Bible, relegating it to the same category as other religious
                  writings and purportedly sacred texts. This view makes it easy
                  for individuals to accept biblical teaching that they resonate
                  with while simultaneously rejecting any biblical teaching that
                  is out of step with their own personal views or broader
                  cultural values.
                </P>

                <P>
                  The Bible, however, is a unified message from the one true
                  God. As such, it is to be embraced in all its fullness as
                  God’s perfect revelation to humankind (Prov. 30:5; 2 Tim.
                  3:16; 2 Peter 1:19–21). We must conform our lives to Scripture
                  rather than twist Scripture to suit our lives.
                </P>

                <P>
                  This is the clearest and most consistent trend revealed by the
                  State of Theology survey since it began in 2014.
                </P>

                <Statement>
                  <div>
                    <H4>Statement No. 16</H4>
                    <StatementHeading>
                      The Bible, like all sacred writings, contains helpful
                      accounts of ancient myths but is not literally true.
                    </StatementHeading>
                  </div>
                  <StatementFinding>
                    <strong>U.S. Adult Finding:</strong>
                    2014: 41% agree
                    <br />
                    2016: 44% agree
                    <br />
                    2018: 47% agree
                    <br />
                    2020: 48% agree
                    <br />
                    2022: 53% agree
                  </StatementFinding>
                </Statement>

                <Chart>
                  <LazyLoad offset={200}>
                    <BarStackedChartGroup
                      data={[
                        ["Q17_1", data["2014"].responses],
                        ["Q24_1", data["2016"].responses],
                        ["Q23_1", data["2018"].responses],
                        ["Q25_1", data["2020"].responses],
                        ["Q25_1", data["2022"].responses],
                      ]}
                      locale={locale}
                      search={searchAll}
                      population="U.S. Adult Respondents"
                      years={["2014", "2016", "2018", "2020", "2022"]}
                    />
                  </LazyLoad>
                </Chart>

                <More>
                  <a href="/data-explorer/2022/16">View in Data Explorer</a>
                </More>

                <H3>Secularization</H3>

                <P>
                  The Bible reveals to us God’s purposes and requirements for
                  human relationships and sexuality. God created humans as male
                  and female (Gen. 1:27), and He instituted marriage—a covenant
                  relationship between one biological man and one biological
                  woman—as the only appropriate place for sexual activity (Heb.
                  13:4).
                </P>

                <P>
                  Despite Scripture’s teaching on God’s will for human identity
                  and sexual ethics, U.S. adults increasingly affirm the “right”
                  of an individual to choose his or her own gender identity
                  despite one’s biological sex. The surveys from 2016 to 2020
                  showed this view holding steady, but the 2022 survey reveals
                  that it is gaining traction.
                </P>

                <Statement>
                  <div>
                    <H4>Statement No. 27</H4>
                    <StatementHeading>
                      Gender identity is a matter of choice.
                    </StatementHeading>
                  </div>
                  <StatementFinding>
                    <strong>U.S. Adult Finding:</strong>
                    2016: 38% agree
                    <br />
                    2018: 38% agree
                    <br />
                    2020: 38% agree
                    <br />
                    2022: 42% agree
                  </StatementFinding>
                </Statement>

                <Chart>
                  <LazyLoad offset={200}>
                    <BarStackedChartGroup
                      data={[
                        ["Q29_3", data["2016"].responses],
                        ["Q27_3", data["2018"].responses],
                        ["Q29_3", data["2020"].responses],
                        ["Q28_3", data["2022"].responses],
                      ]}
                      locale={locale}
                      search={searchAll}
                      population="U.S. Adult Respondents"
                      years={["2016", "2018", "2020", "2022"]}
                    />
                  </LazyLoad>
                </Chart>

                <More>
                  <a href="/data-explorer/2022/27">View in Data Explorer</a>
                </More>

                <P>
                  Additionally, more and more Americans take the view that the
                  Bible’s commands against homosexuality are no longer
                  applicable in society today. While there has been some
                  variability, 2022 reveals an overall increasing secular trend
                  over the years.
                </P>

                <Statement>
                  <div>
                    <H4>Statement No. 28</H4>
                    <StatementHeading>
                      The Bible’s condemnation of homosexual behavior doesn’t
                      apply today.
                    </StatementHeading>
                  </div>
                  <StatementFinding>
                    <strong>U.S. Adult Finding:</strong>
                    2016: 42% agree
                    <br />
                    2018: 44% agree
                    <br />
                    2020: 40% agree
                    <br />
                    2022: 46% agree
                  </StatementFinding>
                </Statement>

                <Chart>
                  <LazyLoad offset={200}>
                    <BarStackedChartGroup
                      data={[
                        ["Q29_5", data["2016"].responses],
                        ["Q27_4", data["2018"].responses],
                        ["Q29_4", data["2020"].responses],
                        ["Q28_4", data["2022"].responses],
                      ]}
                      locale={locale}
                      search={searchAll}
                      population="U.S. Adult Respondents"
                      years={["2016", "2018", "2020", "2022"]}
                    />
                  </LazyLoad>
                </Chart>

                <More>
                  <a href="/data-explorer/2022/28">View in Data Explorer</a>
                </More>

                <H2>U.S. Evangelicals: Trends</H2>

                <P>
                  The 2022 survey results for U.S. evangelicals reveal
                  concerning trends related to the exclusivity and deity of
                  Jesus Christ, the historicity and divine nature of Scripture,
                  objective truth, gender identity, and homosexuality. At the
                  same time, encouraging trends include evangelicals’
                  increasingly biblical views on abortion and sex outside
                  of marriage.
                </P>

                <H3>God</H3>

                <P>
                  Key to orthodox Christianity is Jesus’ own assertion that He
                  alone is “the way, and the truth, and the life,” and that “no
                  one comes to the Father <em>except</em> through [Him]” (John
                  14:6, emphasis added). Trends over time and the 2022 survey
                  results reveal an increasingly unbiblical belief among
                  evangelicals that God is pleased by worship that comes from
                  those outside the Christian faith.
                </P>

                <Statement>
                  <div>
                    <H4>Statement No. 3</H4>
                    <StatementHeading>
                      God accepts the worship of all religions, including
                      Christianity, Judaism, and Islam.
                    </StatementHeading>
                  </div>
                  <StatementFinding>
                    <strong>U.S. Evangelical Finding:</strong>
                    2016: 48% agree
                    <br />
                    2018: 51% agree
                    <br />
                    2020: 42% agree
                    <br />
                    2022: 56% agree
                  </StatementFinding>
                </Statement>

                <Chart>
                  <LazyLoad offset={200}>
                    <BarStackedChartGroup
                      data={[
                        ["Q20_2", data["2016"].responses],
                        ["Q19_3", data["2018"].responses],
                        ["Q21_3", data["2020"].responses],
                        ["Q21_3", data["2022"].responses],
                      ]}
                      locale={locale}
                      search={searchEvangelical}
                      population="U.S. Evangelical Respondents"
                      years={["2016", "2018", "2020", "2022"]}
                    />
                  </LazyLoad>
                </Chart>

                <More>
                  <a href={`/data-explorer/2022/3${evangelicalQueryString}`}>
                    View in Data Explorer
                  </a>
                </More>

                <P>
                  This year’s survey also revealed a significant increase in
                  evangelicals who deny Jesus’ divinity. Such a belief is
                  contrary to Scripture, which affirms from beginning to end
                  that Jesus is indeed God (John 1:1; 8:58; Rom. 9:5; Heb.
                  1:1-4).
                </P>

                <Statement>
                  <div>
                    <H4>Statement No. 7</H4>
                    <StatementHeading>
                      Jesus was a great teacher, but he was not God.
                    </StatementHeading>
                  </div>
                  <StatementFinding>
                    <strong>U.S. Evangelical Finding:</strong>
                    2020: 30% agree
                    <br />
                    2022: 43% agree
                  </StatementFinding>
                </Statement>

                <Chart>
                  <LazyLoad offset={200}>
                    <BarStackedChartGroup
                      data={[
                        ["Q22_3", data["2020"].responses],
                        ["Q22_3", data["2022"].responses],
                      ]}
                      locale={locale}
                      search={searchEvangelical}
                      population="U.S. Evangelical Respondents"
                      years={["2020", "2022"]}
                    />
                  </LazyLoad>
                </Chart>

                <More>
                  <a href={`/data-explorer/2022/7${evangelicalQueryString}`}>
                    View in Data Explorer
                  </a>
                </More>

                <H3>Truth</H3>

                <P>
                  Embracing the reality of objective truth is necessary to a
                  right and proper understanding of the world. Scripture affirms
                  that God is truth (Num. 23:19; John 1:14; 14:6; 16:13), and
                  because the Bible is His Word, Scripture is truth as well (Ps.
                  119:160; John 17:17; 2 Tim. 2:15). Despite the testimony of
                  Scripture, evangelicals increasingly believe that the Bible is
                  not literally true.
                </P>

                <Statement>
                  <div>
                    <H4>Statement No. 16</H4>
                    <StatementHeading>
                      The Bible, like all sacred writings, contains helpful
                      accounts of ancient myths but is not literally true.
                    </StatementHeading>
                  </div>
                  <StatementFinding>
                    <strong>U.S. Evangelical Finding:</strong>
                    2016: 17% agree
                    <br />
                    2018: 23% agree
                    <br />
                    2020: 15% agree
                    <br />
                    2022: 26% agree
                  </StatementFinding>
                </Statement>

                <Chart>
                  <LazyLoad offset={200}>
                    <BarStackedChartGroup
                      data={[
                        ["Q24_1", data["2016"].responses],
                        ["Q23_1", data["2018"].responses],
                        ["Q25_1", data["2020"].responses],
                        ["Q25_1", data["2022"].responses],
                      ]}
                      locale={locale}
                      search={searchEvangelical}
                      population="U.S. Evangelical Respondents"
                      years={["2016", "2018", "2020", "2022"]}
                    />
                  </LazyLoad>
                </Chart>

                <More>
                  <a href={`/data-explorer/2022/16${evangelicalQueryString}`}>
                    View in Data Explorer
                  </a>
                </More>

                <P>
                  A rising disbelief in the Bible’s literal truth may help us
                  understand why American evangelicals also increasingly believe
                  that religious faith is a subjective experience rather than an
                  objective reality.
                </P>

                <Statement>
                  <div>
                    <H4>Statement No. 31</H4>
                    <StatementHeading>
                      Religious belief is a matter of personal opinion; it is
                      not about objective truth.
                    </StatementHeading>
                  </div>
                  <StatementFinding>
                    <strong>U.S. Evangelical Finding:</strong>
                    2018: 32% agree
                    <br />
                    2020: 23% agree
                    <br />
                    2022: 38% agree
                  </StatementFinding>
                </Statement>

                <Chart>
                  <LazyLoad offset={200}>
                    <BarStackedChartGroup
                      data={[
                        ["Q27_5", data["2018"].responses],
                        ["Q29_5", data["2020"].responses],
                        ["Q29_3", data["2022"].responses],
                      ]}
                      locale={locale}
                      search={searchEvangelical}
                      population="U.S. Evangelical Respondents"
                      years={["2018", "2020", "2022"]}
                    />
                  </LazyLoad>
                </Chart>

                <More>
                  <a href={`/data-explorer/2022/31${evangelicalQueryString}`}>
                    View in Data Explorer
                  </a>
                </More>

                <H3>Sexual Ethics</H3>

                <P>
                  The changes in sexual ethics in the United States are
                  occurring at an alarmingly rapid pace. However, one
                  encouraging finding is that evangelicals affirm a biblical
                  view of fornication and adultery in greater numbers.
                </P>

                <Statement>
                  <div>
                    <H4>Statement No. 25</H4>
                    <StatementHeading>
                      Sex outside of traditional marriage is a sin.
                    </StatementHeading>
                  </div>
                  <StatementFinding>
                    <strong>U.S. Evangelical Finding:</strong>
                    2016: 91% agree
                    <br />
                    2018: 89% agree
                    <br />
                    2020: 90% agree
                    <br />
                    2022: 94% agree
                  </StatementFinding>
                </Statement>

                <Chart>
                  <LazyLoad offset={200}>
                    <BarStackedChartGroup
                      data={[
                        ["Q29_1", data["2016"].responses],
                        ["Q27_1", data["2018"].responses],
                        ["Q29_1", data["2020"].responses],
                        ["Q28_1", data["2022"].responses],
                      ]}
                      locale={locale}
                      search={searchEvangelical}
                      population="U.S. Evangelical Respondents"
                      years={["2016", "2018", "2020", "2022"]}
                    />
                  </LazyLoad>
                </Chart>

                <More>
                  <a href={`/data-explorer/2022/25${evangelicalQueryString}`}>
                    View in Data Explorer
                  </a>
                </More>

                <P>
                  While evangelicals are more likely than U.S. adults in general
                  to affirm a biblical sexual ethic, in the areas of gender
                  identity and homosexuality, a significant rise of an
                  unbiblical worldview is apparent, especially in the
                  2022 survey.
                </P>

                <Statement>
                  <div>
                    <H4>Statement No. 27</H4>
                    <StatementHeading>
                      Gender identity is a matter of choice.
                    </StatementHeading>
                  </div>
                  <StatementFinding>
                    <strong>U.S. Evangelical Finding:</strong>
                    2016: 32% agree
                    <br />
                    2018: 30% agree
                    <br />
                    2020: 22% agree
                    <br />
                    2022: 37% agree
                  </StatementFinding>
                </Statement>

                <Chart>
                  <LazyLoad offset={200}>
                    <BarStackedChartGroup
                      data={[
                        ["Q29_3", data["2016"].responses],
                        ["Q27_3", data["2018"].responses],
                        ["Q29_3", data["2020"].responses],
                        ["Q28_3", data["2022"].responses],
                      ]}
                      locale={locale}
                      search={searchEvangelical}
                      population="U.S. Evangelical Respondents"
                      years={["2016", "2018", "2020", "2022"]}
                    />
                  </LazyLoad>
                </Chart>

                <More>
                  <a href={`/data-explorer/2022/27${evangelicalQueryString}`}>
                    View in Data Explorer
                  </a>
                </More>

                <P>
                  The rise of unbiblical views among American evangelicals on
                  the subjects of gender and sexuality may indicate the
                  influence of a secular worldview that is making greater
                  inroads into the church.
                </P>

                <Statement>
                  <div>
                    <H4>Statement No. 28</H4>
                    <StatementHeading>
                      The Bible’s condemnation of homosexual behavior doesn’t
                      apply today.
                    </StatementHeading>
                  </div>
                  <StatementFinding>
                    <strong>U.S. Evangelical Finding:</strong>
                    2016: 19% agree
                    <br />
                    2018: 20% agree
                    <br />
                    2020: 11% agree
                    <br />
                    2022: 28% agree
                  </StatementFinding>
                </Statement>

                <Chart>
                  <LazyLoad offset={200}>
                    <BarStackedChartGroup
                      data={[
                        ["Q29_5", data["2016"].responses],
                        ["Q27_4", data["2018"].responses],
                        ["Q29_4", data["2020"].responses],
                        ["Q28_4", data["2022"].responses],
                      ]}
                      locale={locale}
                      search={searchEvangelical}
                      population="U.S. Evangelical Respondents"
                      years={["2016", "2018", "2020", "2022"]}
                    />
                  </LazyLoad>
                </Chart>

                <More>
                  <a href={`/data-explorer/2022/28${evangelicalQueryString}`}>
                    View in Data Explorer
                  </a>
                </More>

                <H3>Abortion</H3>

                <P>
                  The Bible teaches the personhood of those in the womb (Ps.
                  139:13, 16; Luke 1:41, 44). As such, the practice of abortion
                  is the murder of a human being who is an image bearer of God.
                  Past survey results among evangelicals have remained fairly
                  consistent since 2016, with an uptick in 2022 of more
                  evangelicals asserting that abortion is a sin. While the
                  reasons for this positive trend are unknown, it is encouraging
                  to see more evangelicals affirming the personhood of human
                  beings in the womb.
                </P>

                <Statement>
                  <div>
                    <H4>Statement No. 26</H4>
                    <StatementHeading>Abortion is a sin.</StatementHeading>
                  </div>
                  <StatementFinding>
                    <strong>U.S. Evangelical Finding:</strong>
                    2016: 87% agree
                    <br />
                    2018: 88% agree
                    <br />
                    2020: 88% agree
                    <br />
                    2022: 91% agree
                  </StatementFinding>
                </Statement>

                <Chart>
                  <LazyLoad offset={200}>
                    <BarStackedChartGroup
                      data={[
                        ["Q29_2", data["2016"].responses],
                        ["Q27_2", data["2018"].responses],
                        ["Q29_2", data["2020"].responses],
                        ["Q28_2", data["2022"].responses],
                      ]}
                      locale={locale}
                      search={searchEvangelical}
                      population="U.S. Evangelical Respondents"
                      years={["2016", "2018", "2020", "2022"]}
                    />
                  </LazyLoad>
                </Chart>

                <More>
                  <a href={`/data-explorer/2022/26${evangelicalQueryString}`}>
                    View in Data Explorer
                  </a>
                </More>

                <P>
                  Evangelicals were defined by LifeWay Research as people who
                  strongly agreed with the following four statements:
                </P>

                <UL>
                  <li>
                    The Bible is the highest authority for what I believe.
                  </li>
                  <li>
                    It is very important for me personally to encourage
                    non-Christians to trust Jesus Christ as their Savior.
                  </li>
                  <li>
                    Jesus Christ’s death on the cross is the only sacrifice that
                    could remove the penalty of my sin.
                  </li>
                  <li>
                    Only those who trust in Jesus Christ alone as their Savior
                    receive God’s free gift of eternal salvation.
                  </li>
                </UL>

                <P>
                  Respondents can also be identified as evangelical in the State
                  of Theology <a href="/data-explorer">data explorer</a> based
                  on their supplied church affiliation.
                </P>

                <H2>Conclusion</H2>

                <P>
                  The 2022 State of Theology survey reveals that Americans
                  increasingly reject the divine origin and complete accuracy of
                  the Bible. With no enduring plumb line of absolute truth to
                  conform to, U.S. adults are also increasingly holding to
                  unbiblical worldviews related to human sexuality. In the
                  evangelical sphere, doctrines including the deity and
                  exclusivity of Jesus Christ, as well as the inspiration and
                  authority of the Bible, are increasingly being rejected. While
                  positive trends are present, including evangelicals’ views on
                  abortion and sex outside of marriage, an inconsistent biblical
                  ethic is also evident, with more evangelicals embracing a
                  secular worldview in the areas of homosexuality and
                  gender identity.
                </P>

                <P>
                  These results convey the ongoing need for the church to be
                  engaged in apologetics, helping unbelievers by providing a
                  well-reasoned defense of the Christian faith, and helping
                  believers by strengthening their clarity and conviction
                  regarding why they believe what they do. Additionally, the
                  people of God must continue to obey the Great Commission by
                  communicating the whole counsel of God in biblical evangelism
                  and discipleship. The need is great, but the power and
                  promises of God can equip the church to bring truth and light
                  to a deceived and dark world.
                </P>

                <ExplorePromo />
                <Footer />
              </Content>
            </Container>
          );
        }}
      </SurveyData>
    </React.Fragment>
  );
};

export default KeyFindings;
